// src/components/ProjectionChart.js
import React from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { formatCurrency } from '../utils/formatting';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const ProjectionChart = ({ projectionData, targetAmount, minimumThreshold }) => {
    // Skip some data points if there are too many to avoid overcrowding
    const displayInterval = projectionData.length > 12 ? 2 : 1;

    const labels = projectionData
        .filter((_, index) => index % displayInterval === 0)
        .map(data => data.month);

    const balanceData = projectionData
        .filter((_, index) => index % displayInterval === 0)
        .map(data => data.balance);

    const data = {
        labels,
        datasets: [
            {
                label: 'Projected Balance',
                data: balanceData,
                borderColor: 'rgb(53, 162, 235)',
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
                tension: 0.3,
            },
            {
                label: 'Target Amount',
                data: Array(labels.length).fill(targetAmount),
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.5)',
                borderDash: [5, 5],
                pointRadius: 0,
                tension: 0,
            },
            {
                label: 'Minimum Threshold',
                data: Array(labels.length).fill(minimumThreshold),
                borderColor: 'rgb(234, 179, 8)',
                backgroundColor: 'rgba(234, 179, 8, 0.5)',
                borderDash: [5, 5],
                pointRadius: 0,
                tension: 0,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += formatCurrency(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                ticks: {
                    callback: function (value) {
                        return formatCurrency(value, false);
                    }
                }
            }
        }
    };

    return (
        <div className="w-full h-80">
            <Line data={data} options={options} />
            <div className="flex justify-center mt-4 text-sm gap-4">
                <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
                    <span>Projected Balance</span>
                </div>
                <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
                    <span>Target ({formatCurrency(targetAmount)})</span>
                </div>
                <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
                    <span>Minimum ({formatCurrency(minimumThreshold)})</span>
                </div>
            </div>
        </div>
    );
};

ProjectionChart.propTypes = {
    projectionData: PropTypes.arrayOf(
        PropTypes.shape({
            month: PropTypes.string.isRequired,
            balance: PropTypes.number.isRequired,
        })
    ).isRequired,
    targetAmount: PropTypes.number.isRequired,
    minimumThreshold: PropTypes.number.isRequired,
};

export default ProjectionChart;